import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";

interface VideoPayload {
  video?: {
    url: string;
    content_type?: string;
    file_name?: string;
    file_size?: number;
  };
}

interface StatusResponse {
  status: string;
  payload?: VideoPayload;
}

interface VideoItem {
  _id: string;
  userId: string;
  modelId: string;
  status: string; // "OK", "ERROR", etc.
  statusResponse?: StatusResponse;
  isGenerated: boolean;
}

interface VideoWebSocketContextProps {
  status: string | null;
  videoItem: VideoItem | null;
  socketPrompt: string | null;
  socketImage: string | null;
  sendMessage: (message: any) => void;
  generatedStatus: string | null;
  videoGenerated: string | null;
  responseData: any;
  reconnectWebSocket: () => void;
}

const VideoWebSocketContext = createContext<VideoWebSocketContextProps | undefined>(undefined);

export const VideoSocketProvider: React.FC<{ userId: string; modelId: string; children: React.ReactNode }> = ({
  userId,
  modelId,
  children,
}) => {
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<NodeJS.Timeout | null>(null); // ✅ Added here
  const [status, setStatus] = useState<string | null>(null);
  const [videoItem, setVideoItem] = useState<VideoItem | null>(null);
  const [socketPrompt, setSocketPrompt] = useState<string | null>(null);  
  const [socketImage, setSocketImage] = useState<string | null>(null);
  const [generatedStatus, setGeneratedStatus] = useState<string | null>(null);
  const [videoGenerated, setVideoGenerated] = useState<string | null>(null);
  const [responseData, setResponseData] = useState<any>([]);

  const socketURL = `${process.env.REACT_APP_WEB_SOCKET_URL}video?userId=${userId}&modelId=${modelId}`;

  const connectWebSocket = useCallback(() => {
    const socket = new WebSocket(socketURL);

    socket.onopen = () => {
      console.log("WebSocket connected:", socket.url);
    };

    socket.onmessage = (event) => {
      console.log("WebSocket message received:", event.data);
      try {
        const parsed = JSON.parse(event.data);
        const status = parsed?.data?.[0]?.status;
        const videoItem = parsed?.data?.[0]?.videoUrl || parsed?.data?.[0]?.statusResponse?.payload?.video?.url;
        const socketPromptItem = parsed?.data?.[0]?.inputData?.prompt;
        const socketImageItem = parsed?.data?.[0]?.inputData?.imageUrl;
        const generatedStatus = parsed?.data?.[0]?.isGenerated;
        const videoGenerated = parsed?.data?.[0]?.isGenerated || "";
          setStatus(status);
        if (status === "OK") {
          setVideoItem(videoItem);
          setSocketPrompt(socketPromptItem);
          setSocketImage(socketImageItem);
          setGeneratedStatus(generatedStatus);
          setVideoGenerated(videoGenerated);
          const newObj = parsed?.data?.[0];
          setResponseData((prev: any[]) => {
            if (!newObj || !newObj._id) return prev;
            if (prev.some(item => item._id === newObj._id)) return prev;
            return [...prev, newObj];
          });
        }
      } catch (err) {
        console.error("Failed to parse WebSocket message:", err);
      }
    };

    socket.onerror = (err) => {
      console.error("WebSocket error:", err);
    };

    socketRef.current = socket;
  }, [socketURL]);

  const closeWebSocket = () => {
    console.log("Closing WebSocket...");
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
  };

 const reconnectWebSocket = () => {
    closeWebSocket();
      console.log("Reconnecting WebSocket...");
    connectWebSocket();
  };

 useEffect(() => {
    console.log("ids", userId, modelId);
    if (!userId || !modelId) return;
    connectWebSocket();

  }, [userId, modelId,connectWebSocket]);

  const sendMessage = (message: any) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(message));
    } else {
      console.warn("WebSocket is not open. Message not sent:", message);
    }
  };

  return (
    <VideoWebSocketContext.Provider
      value={{
        status,
        sendMessage,
        videoItem,
        socketPrompt,
        socketImage,
        generatedStatus,
        videoGenerated,
        responseData,
        reconnectWebSocket,
      }}
    >
      {children}
    </VideoWebSocketContext.Provider>
  );
};

export const useVideoWebSocket = () => {
  const context = useContext(VideoWebSocketContext);
  if (!context) {
    throw new Error("useVideoWebSocket must be used within a VideoSocketProvider");
  }
  return context;
};